/**
 * Reads the stored courier timeline (`funerals.courier_status`), written by the
 * backend's courier-tracking mapper:
 *
 *   "Booked - 9:35 AM · … · Not delivered - 4:43 PM (retour) ·
 *    Returned to sender - 4:56 PM (signed back in by julia)"
 *
 * A call that came back must never read as delivered, so the deliveries view
 * asks this first and renders the return in red.
 */
export type CourierProblem =
  | { kind: 'returned'; time: string; by?: string }
  | { kind: 'not_delivered'; time: string; marker?: string };

const milestones = (status: string) => status.split(' · ').map((s) => s.trim());

const parse = (milestone: string, label: string) => {
  if (!milestone.startsWith(`${label} - `)) return null;
  const rest = milestone.slice(label.length + 3);
  const m = rest.match(/^([^()]+?)\s*(?:\((.*)\))?$/);
  return { time: (m?.[1] ?? rest).trim(), note: m?.[2]?.trim() };
};

export const courierProblem = (status?: string | null): CourierProblem | null => {
  if (!status) return null;
  const parts = milestones(status);
  for (const p of parts) {
    const r = parse(p, 'Returned to sender');
    if (r) {
      const by = r.note?.replace(/^signed back in by\s*/i, '');
      return { kind: 'returned', time: r.time, by: by || undefined };
    }
  }
  for (const p of parts) {
    const r = parse(p, 'Not delivered');
    if (r) return { kind: 'not_delivered', time: r.time, marker: r.note };
  }
  return null;
};

/** "Returned to sender · 4:56 PM · signed back in by julia". */
export const courierProblemLabel = (p: CourierProblem): string =>
  p.kind === 'returned'
    ? `Returned to sender · ${p.time}${p.by ? ` · signed back in by ${p.by}` : ''}`
    : `Not delivered · ${p.time}${p.marker ? ` · “${p.marker}”` : ''} · returning`;
